import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Calendar, Clock, MapPin, Users, Phone,
  MessageSquare, CheckCircle, XCircle, AlertTriangle, ChevronRight,
} from 'lucide-react'
import { clsx } from 'clsx'
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query'
import {
  providerDashboardService,
  type ProviderAppointmentItem,
} from '@lib/supabase/providerDashboard.service'
import Button from '@components/web/Button'
import { useProviderAuth } from '@hooks/useProviderAuth'
import { queryKeys } from '@lib/queryKeys'

const LOAD_STEP = 4
const FETCH_BATCH_SIZE = 8

type FilterTab = 'All' | 'Requested' | 'Scheduled' | 'Completed' | 'Cancelled'
const TABS: FilterTab[] = ['All', 'Requested', 'Scheduled', 'Completed', 'Cancelled']

const STATUS_STYLE: Record<string, string> = {
  requested: 'bg-yellow-50 text-yellow-800 border-yellow-200',
  scheduled: 'bg-emerald-50 text-emerald-800 border-emerald-200',
  completed: 'bg-blue-50 text-blue-800 border-blue-200',
  cancelled: 'bg-red-50 text-red-800 border-red-200',
}

function normalizeStatus(status?: string | null) {
  return (status || '').toLowerCase().trim()
}

function formatCurrency(n: number) {
  return new Intl.NumberFormat('en-NG', {
    style: 'currency', currency: 'NGN', maximumFractionDigits: 0,
  }).format(n)
}
function formatDate(d: string) {
  return new Date(d).toLocaleDateString('en-US', {
    weekday: 'short', month: 'short', day: 'numeric', year: 'numeric',
  })
}
function formatTime(t?: string) {
  if (!t) return ''
  const [h, m] = t.split(':')
  let hour = parseInt(h, 10)
  const ampm = hour >= 12 ? 'PM' : 'AM'
  hour = hour % 12 || 12
  return `${hour}:${m} ${ampm}`
}

export default function ProviderAppointments() {
  const navigate = useNavigate()
  const { provider } = useProviderAuth()
  const [activeTab, setActiveTab] = useState<FilterTab>('All')
  const queryClient = useQueryClient()
  const [actionLoading, setActionLoading] = useState<string | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)
  const [visibleCount, setVisibleCount] = useState(LOAD_STEP)

  const statusParam = activeTab === 'All' ? undefined : activeTab
  const appointmentsQuery = useInfiniteQuery({
    queryKey: queryKeys.provider.appointmentPages(provider?.id, statusParam),
    queryFn: ({ pageParam = 0 }) => providerDashboardService.getAppointmentsPage(statusParam, FETCH_BATCH_SIZE, pageParam, 'Chat Consultation'),
    enabled: !!provider?.id,
    initialPageParam: 0,
    getNextPageParam: (lastPage) => lastPage.nextOffset ?? undefined,
  })

  const appointments = useMemo(() => {
    const seen = new Set<string>()
    const merged: ProviderAppointmentItem[] = []

    for (const page of appointmentsQuery.data?.pages ?? []) {
      for (const appointment of page.appointments) {
        if (seen.has(appointment.id)) continue
        seen.add(appointment.id)
        merged.push(appointment)
      }
    }

    return merged
  }, [appointmentsQuery.data])

  const isLoading = appointmentsQuery.isPending
  const isLoadingMore = appointmentsQuery.isFetchingNextPage
  const hasMore = Boolean(appointmentsQuery.hasNextPage)

  const handleAction = async (id: string, status: string, reason?: string) => {
    setActionLoading(id + status)
    setActionError(null)
    const result = await providerDashboardService.updateStatus(id, status, reason)
    setActionLoading(null)
    if (result.success) {
      await queryClient.invalidateQueries({ queryKey: queryKeys.provider.appointmentPages(provider?.id) })
    } else {
      setActionError(result.error ?? 'Action failed.')
    }
  }

  const handleLoadMore = async () => {
    if (visibleCount < appointments.length) {
      setVisibleCount((count) => count + LOAD_STEP)
      return
    }

    if (!hasMore || isLoadingMore) return

    const result = await appointmentsQuery.fetchNextPage()
    if (!result.isError) {
      setVisibleCount((count) => count + LOAD_STEP)
    }
  }

  const displayedAppointments = appointments.slice(0, visibleCount)
  const canLoadMore = visibleCount < appointments.length || hasMore

  return (
    <div className="p-4 sm:p-6 space-y-5">
      <div>
        <h1 className="text-xl font-bold text-foreground">Appointments</h1>
        <p className="text-sm text-muted-foreground">
          Showing {displayedAppointments.length} {activeTab === 'All' ? 'appointments' : activeTab.toLowerCase()}
        </p>
      </div>

      {actionError && (
        <div className="flex items-center gap-2 px-3 py-2.5 bg-destructive/10 border border-destructive/20 rounded-xl text-sm text-destructive">
          <AlertTriangle size={14} /> {actionError}
          <button onClick={() => setActionError(null)} className="ml-auto text-xs underline">Dismiss</button>
        </div>
      )}

      {/* Filter tabs */}
      <div className="flex gap-2 overflow-x-auto pb-1">
        {TABS.map((tab) => (
            <button
              key={tab}
              onClick={() => {
                setActiveTab(tab)
                setVisibleCount(LOAD_STEP)
              }}
              className={clsx(
                'flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap border transition-all',
                activeTab === tab
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'bg-card border-border text-muted-foreground hover:bg-muted'
              )}
            >
              {tab}
            </button>
        ))}
      </div>

      {/* List */}
      {isLoading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-card border border-border p-5 animate-pulse space-y-3">
              <div className="flex gap-3">
                <div className="w-12 h-12 rounded-full bg-muted shrink-0" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-muted rounded w-1/2" />
                  <div className="h-3 bg-muted rounded w-1/3" />
                </div>
              </div>
              <div className="h-3 bg-muted rounded w-full" />
              <div className="h-3 bg-muted rounded w-2/3" />
            </div>
          ))}
        </div>
      ) : displayedAppointments.length === 0 ? (
        <div className="bg-card border border-border py-16 text-center">
          <Calendar size={36} className="text-muted-foreground/40 mx-auto mb-3" />
          <p className="font-medium text-foreground">
            No {activeTab === 'All' ? '' : activeTab.toLowerCase() + ' '}appointments
          </p>
          <p className="text-sm text-muted-foreground mt-1">
            {activeTab === 'Requested' ? 'New requests will appear here' : 'Check back later'}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {displayedAppointments.map((appt) => {
            const isLoadingThis = actionLoading?.startsWith(appt.id) ?? false
            const isChatConsultation = appt.serviceType === 'Chat Consultation'
            const normalizedStatus = normalizeStatus(appt.status)

            return (
              <div key={appt.id} className="bg-card border border-border p-5 space-y-4">
                {/* Patient header — click to view details */}
                <button
                  className="flex items-start gap-3 w-full text-left hover:opacity-80 transition-opacity"
                  onClick={() => navigate(`/provider/appointments/${appt.id}`)}
                >
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center shrink-0 overflow-hidden">
                    {appt.patientImage ? (
                      <img src={appt.patientImage} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <Users size={20} className="text-primary" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-foreground">{appt.patientName}</p>
                    <p className="text-sm text-muted-foreground">{appt.serviceType}</p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className={clsx(
                      'text-xs px-2.5 py-1 rounded-full border font-medium',
                      STATUS_STYLE[normalizedStatus] ?? 'bg-muted text-muted-foreground border-border'
                    )}>
                      {appt.status}
                    </span>
                    <ChevronRight size={16} className="text-muted-foreground" />
                  </div>
                </button>

                {/* Details grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-muted-foreground">
                  <div className="flex items-center gap-2">
                    <Calendar size={14} />
                    <span>{formatDate(appt.scheduledDate)}</span>
                  </div>
                  {appt.scheduledTime && (
                    <div className="flex items-center gap-2">
                      <Clock size={14} />
                      <span>{formatTime(appt.scheduledTime)}</span>
                    </div>
                  )}
                  {appt.location && (
                    <div className="flex items-center gap-2">
                      <MapPin size={14} />
                      <span className="truncate">{appt.location}</span>
                    </div>
                  )}
                  {appt.patientPhone && (
                    <div className="flex items-center gap-2">
                      <Phone size={14} />
                      <span>{appt.patientPhone}</span>
                    </div>
                  )}
                </div>

                {/* Notes */}
                {appt.notes && (
                  <div className="bg-muted rounded-xl px-3 py-2">
                    <p className="text-xs text-muted-foreground">
                      <span className="font-medium">Notes:</span> {appt.notes}
                    </p>
                  </div>
                )}

                {/* Cost */}
                <div className="flex items-center justify-between text-sm border-t border-border pt-3">
                  <span className="text-muted-foreground">Consultation fee</span>
                  <span className="font-semibold text-foreground">{formatCurrency(appt.totalCost)}</span>
                </div>

                {/* Actions */}
                {(normalizedStatus === 'requested' || normalizedStatus === 'scheduled') && (
                  <div className="flex flex-wrap gap-2">
                    {/* Chat Consultation — open chat */}
                    {isChatConsultation && normalizedStatus === 'scheduled' && (
                      <Button size="sm" onClick={() => navigate(`/provider/chat/${appt.id}`, { state: { backPath: '/provider/appointments' } })}>
                        <MessageSquare size={14} className="mr-1.5" /> Open Chat
                      </Button>
                    )}

                    {/* Requested → Accept / Decline */}
                    {normalizedStatus === 'requested' && (
                      <>
                        <Button
                          size="sm"
                          isLoading={actionLoading === appt.id + 'Scheduled'}
                          disabled={isLoadingThis}
                          onClick={() => handleAction(appt.id, 'Scheduled')}
                        >
                          <CheckCircle size={14} className="mr-1.5" /> Accept
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          isLoading={actionLoading === appt.id + 'Cancelled'}
                          disabled={isLoadingThis}
                          onClick={() => {
                            const reason = window.prompt('Reason for declining (optional):') ?? undefined
                            handleAction(appt.id, 'Cancelled', reason)
                          }}
                          className="text-destructive border-destructive/30 hover:bg-destructive/5"
                        >
                          <XCircle size={14} className="mr-1.5" /> Decline
                        </Button>
                      </>
                    )}

                  </div>
                )}
              </div>
            )
          })}

          {canLoadMore && (
            <div className="pt-2">
              <button
                onClick={handleLoadMore}
                disabled={isLoadingMore}
                className="w-full sm:w-auto px-4 py-2 border border-border text-sm font-medium text-foreground hover:bg-muted transition-colors disabled:opacity-50"
              >
                {isLoadingMore ? 'Loading...' : 'Load 4 More'}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
